import { useEffect } from 'react';
import { useGlobalErrStore } from '../store/errorStore';
import { ErrorCode } from '../api/errorCode';
import { useAuth } from './useApi/useAuth';
import { useStandardAlertStore } from '../store/portal/alertStore';
import { AlertPosition, AlertType, ModalType } from '../enum/EnumRepository';
import { useModalStore } from '../store/portal/modalStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePageOwnerIdentityStore } from '../store/pageOwnerIdentityStore';
import { useTempAlertStore } from '../store/portal/tempAlertStore';
import { FILE_MAX_SIZE_TEXT, TEXTBLOCK_IMAGE_MAX_COUNT } from '../constants/limits';
import { getAlertMessage } from '../constants/alertMessages';
import { resolveCurrentPage } from '../util/currentPage';

export const useGlobalError = () => {
  const { managedErr, clearErr } = useGlobalErrStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { setUnauthorizedAccessTempAlert } = useTempAlertStore();
  const { setStandardModal, clearModal } = useModalStore();
  const { logout } = useAuth();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleRTX = async () => {
    console.log("handleRTX: refresh failed!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('SESSION_EXPIRED'),
      callBack: () => {
        logout();
        clearErr();
      }
    });
  }

  const handleNFR = async () => {
    console.log("handleNFR: There is no data like that!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('NO_MATCHING_MEMBER'),
      callBack: () => {
        navigate('/', { replace: true });
        clearErr();
      }
    });
  }

  const handleNAU = async () => {
    console.log("handleNAU: Wrong Password!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('AUTHENTICATION_FAILED'),
      callBack: () => {
        clearErr();
      }
    });
  }

  const handleAEV = async () => {
    console.log("handleAEV: Already existed value!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('VALUE_ALREADY_IN_USE'),
      callBack: () => {
        clearErr();
      }
    });
  }

  const handleDBE = async () => {
    console.log("handleDBE: Database error!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('DATABASE_CONNECTION_ERROR'),
      callBack: () => {
        clearErr();
      }
    });
  }

  const handleAWS = async () => {
    console.log("handleAWS: Something wrong about AWS!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('IMAGE_UPLOAD_FAILED'),
      callBack: clearErr
    });
  }

  const handleDUK = async () => {
    console.log("handleDUK: This value is already in use.");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('VALUE_ALREADY_IN_USE'),
      callBack: clearErr
    });
  }
  
  const handleRVN = async () => {
    console.log("handleRVN: Required value is empty.");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      callBack: clearErr
    });
  }

  const handleENF = async () => {
    console.log("handleENF: Enum Not Found!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('INVALID_VALUE_REQUESTED'),
      callBack: clearErr
    });
  }

  const handleBFE = async () => {
    console.log("handleBFE: File is too big!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('FILE_TOO_BIG', { maxSize: FILE_MAX_SIZE_TEXT }),
      callBack: clearErr
    });
  }

  const handleNCE = async () => {
    console.log("handleNCE: Network connection error!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('NETWORK_ERROR'),
      callBack: clearErr
    });
  }

  const handleSDN = async () => {
    console.log("handleSDN: Server Down!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('SERVER_NOT_AVAILABLE'),
      callBack: clearErr
    });
  }

  const handleEME = async () => {
    console.log("handleEME: Email send error!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('EMAIL_SEND_FAILED'),
      callBack: clearErr
    });
  }

  const handleEVF = async () => {
    console.log("handleEVF: Email verification failed!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('EMAIL_NOT_VALID'),
      callBack: clearErr
    });
  }

  const handleMPA = async () => {
    console.log("handleMPA: Email Verification");
    const email = managedErr?.value?.split(":")[1].trim();
    setStandardModal({
      modalType: ModalType.VERIFICATION,
      title: 'signup',
      value: email || null,
      handleChange: () => { }
    });
  }

  const handleMVE = async () => {
    console.log("handleMVE: Email Verification Error!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('EMAIL_VERIFICATION_ERROR'),
      callBack: () => {
        clearErr();
        clearModal();
      }
    });
  }

  const handleVIE = async () => {
    console.log("handleVIE: Wrong Verification code!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('INVALID_CODE'),
      callBack: () => {
        clearErr();
        clearModal();
      }
    });
  }

  const handleVEE = async () => {
    console.log("handleVEE: Verification Expired!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('CODE_EXPIRED'),
      callBack: () => {
        clearErr();
      }
    });
  }

  const handleVAE = async () => {
    const email = managedErr?.value?.split(":")[1].trim();
    setStandardModal({
      modalType: ModalType.VERIFICATION,
      title: "find_password",
      value: email || null,
      handleChange: () => { }
    });
  }

  const handleUAA = async () => {
    console.log("handleUAA: Unauthorized access!!");
    setUnauthorizedAccessTempAlert();
    if (
      pageOwner &&
      resolveCurrentPage(pathname).page === 'projectDetail'
    ) {
      navigate(`/@${pageOwner.handle}/projects`, { replace: true });
    } else {
      navigate('/', { replace: true });
    }
    clearErr();
  }

  const handleTLX = async () => {
    console.log("handleTLX: Text Length Exceeded!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('SHORTEN_TEXT'),
      callBack: clearErr
    });
  }

  const handleLME = async () => {
    console.log("handleLME: Limit Exceeded!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('TEXTBLOCK_IMAGE_MAX_EXCEEDED', { maxCount: TEXTBLOCK_IMAGE_MAX_COUNT }),
      callBack: clearErr
    });
  }

  const handleWLW = async () => {
    console.log("handleWLW: WaitList Waiting!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('WAITLIST_WAITING'),
      callBack: () => {
        clearErr();
        clearModal();
      }
    });
  }

  const handleWLP = async () => {
    console.log("handleWLP: WaitList Proposal!!");
    setStandardModal({
      modalType: ModalType.JOIN_WAITLIST,
      title: 'join_waitlist',
      value: null,
      handleChange: () => { }
    });
  }

  const handleIVP = async () => {
    console.log("handleIVP: Invalid Password!!");
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('INVALID_PASSWORD'),
      callBack: clearErr
    });
  }

  const handleGroupMemberLinkError = (
    messageKey:
      | 'AUTHENTICATION_FAILED'
      | 'GROUP_MEMBER_LINK_MEMBER_NOT_FOUND'
      | 'GROUP_MEMBER_LINK_GROUP_ACCOUNT'
      | 'GROUP_MEMBER_LINK_ALREADY_LINKED'
      | 'GROUP_MEMBER_LINK_PASSWORD_NOT_SET'
      | 'GROUP_MEMBER_LINK_TOKEN_EXPIRED'
      | 'GROUP_MEMBER_LINK_TOKEN_INVALID'
      | 'GROUP_MEMBER_LINK_TARGET_MISMATCH'
      | 'GROUP_MEMBER_LINK_PROFILE_CHANGED',
  ) => {
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage(messageKey),
      callBack: clearErr,
    });
  };

  const handleUSX = async (errCode: ErrorCode) => {
    console.log(`handleUSX: User Storage Exceeded!!, ${errCode}`);
    if (!pageOwner) return;
    switch (errCode) {
      case ErrorCode.USX_NONE:
        enqueueAlert({
          type: AlertType.CONFIRM,
          position: AlertPosition.TOP,
          content: getAlertMessage('STORAGE_FULL_FREE'),
          callBack: () => {
            navigate(`/@${pageOwner.handle}/settings`);
            clearErr();
          }
        });
        return;
      case ErrorCode.USX_ACTIVE:
        enqueueAlert({
          type: AlertType.ALERT,
          position: AlertPosition.TOP,
          content: getAlertMessage('STORAGE_FULL_ACTIVE'),
          callBack: clearErr
        });
        return;
    }
  }

  const handleGlobalErr = async () => {
    if (managedErr === null) {
      return;
    }
    console.log("errCode: ", managedErr.errCode);
    switch (managedErr.errCode) {
      case ErrorCode.RTX:
        await handleRTX();
        break;
      case ErrorCode.NFR:
        await handleNFR();
        break;
      case ErrorCode.NAU:
        await handleNAU();
        break;
      case ErrorCode.AEV:
        await handleAEV();
        break;
      case ErrorCode.DBE:
        await handleDBE();
        break;
      case ErrorCode.AWS:
        await handleAWS();
        break;
      case ErrorCode.DUK:
        await handleDUK();
        break;
      case ErrorCode.RVN:
        await handleRVN();
        break;
      case ErrorCode.ENF:
        await handleENF();
        break;
      case ErrorCode.BFE:
        await handleBFE();
        break;
      case ErrorCode.NCE:
        await handleNCE();
        break;
      case ErrorCode.SDN:
        await handleSDN();
        break;
      case ErrorCode.EME:
        await handleEME();
        break;
      case ErrorCode.EVF:
        await handleEVF();
        break;
      case ErrorCode.MPA:
        await handleMPA();
        break;
      case ErrorCode.MVE:
        await handleMVE();
        break;
      case ErrorCode.VIE:
        await handleVIE();
        break;
      case ErrorCode.VEE:
        await handleVEE();
        break;
      case ErrorCode.VAE:
        await handleVAE();
        break;
      case ErrorCode.UAA:
        await handleUAA();
        break;
      case ErrorCode.TLX:
        await handleTLX();
        break;
      case ErrorCode.LME:
        await handleLME();
        break;
      case ErrorCode.WLW:
        await handleWLW();
        break;
      case ErrorCode.WLP:
        await handleWLP();
        break;
      case ErrorCode.IVP:
        await handleIVP();
        break;
      case ErrorCode.GML_MEMBER_NOT_FOUND:
        handleGroupMemberLinkError('GROUP_MEMBER_LINK_MEMBER_NOT_FOUND');
        break;
      case ErrorCode.GML_GROUP_ACCOUNT:
        handleGroupMemberLinkError('GROUP_MEMBER_LINK_GROUP_ACCOUNT');
        break;
      case ErrorCode.GML_INVALID_PASSWORD:
        handleGroupMemberLinkError('AUTHENTICATION_FAILED');
        break;
      case ErrorCode.GML_ALREADY_LINKED:
        handleGroupMemberLinkError('GROUP_MEMBER_LINK_ALREADY_LINKED');
        break;
      case ErrorCode.GML_PASSWORD_NOT_SET:
        handleGroupMemberLinkError('GROUP_MEMBER_LINK_PASSWORD_NOT_SET');
        break;
      case ErrorCode.GML_LINK_TOKEN_EXPIRED:
        handleGroupMemberLinkError('GROUP_MEMBER_LINK_TOKEN_EXPIRED');
        break;
      case ErrorCode.GML_LINK_TOKEN_INVALID:
        handleGroupMemberLinkError('GROUP_MEMBER_LINK_TOKEN_INVALID');
        break;
      case ErrorCode.GML_LINK_TARGET_MISMATCH:
        handleGroupMemberLinkError('GROUP_MEMBER_LINK_TARGET_MISMATCH');
        break;
      case ErrorCode.GML_PROFILE_CHANGED:
        handleGroupMemberLinkError('GROUP_MEMBER_LINK_PROFILE_CHANGED');
        break;
      case ErrorCode.USX_NONE:
        await handleUSX(ErrorCode.USX_NONE);
        break;
      case ErrorCode.USX_ACTIVE:
        await handleUSX(ErrorCode.USX_ACTIVE);
        break;
      case ErrorCode.WEF:
      default:
        enqueueAlert({
          type: AlertType.ALERT,
          position: AlertPosition.TOP,
          content: getAlertMessage('SOMETHING_WENT_WRONG'),
          callBack: clearErr
        })
        return;
    }
  }

  useEffect(() => {
    handleGlobalErr();
  }, [managedErr]);
}
